import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  Bootstrap,
  Department,
  DirectoryUser,
  Reason,
  StageDef,
  StepDTO,
  ToolType,
} from '@pace/contracts';
import { plantToday, type Role, type WorkCalendar } from '@pace/domain';
import { api } from '../lib/api';
import { now, syncServerTime } from '../lib/time';
import { useI18n } from '../i18n';

export const BOOTSTRAP_KEY = ['bootstrap'] as const;

export function useBootstrap() {
  return useQuery({
    queryKey: BOOTSTRAP_KEY,
    queryFn: async () => {
      const sent = Date.now();
      const b = await api<Bootstrap>('/bootstrap');
      syncServerTime(b.serverTime, sent);
      return b;
    },
    staleTime: 5 * 60_000,
    networkMode: 'offlineFirst',
  });
}

export const ROLE_COLOUR: Record<Role, string> = {
  owner: '#9A5B24',
  planner: '#1C3D5A',
  mgmt: '#3B4A44',
  admin: '#5F6B66',
};

export interface Lookups {
  b: Bootstrap;
  tz: string;
  cal: WorkCalendar;
  /** Plant-local date right now. */
  today: () => string;
  stageDef: (id: string) => StageDef | undefined;
  /** Stage name in the chosen language (a step's own label wins, e.g. "T3"). */
  stageName: (s: Pick<StepDTO, 'stageDefId' | 'label'>) => string;
  stageCode: (s: Pick<StepDTO, 'stageDefId' | 'label'>) => string;
  department: (id: string | null | undefined) => Department | undefined;
  deptName: (id: string | null | undefined) => string;
  /** Department that owns a step's stage. */
  deptOf: (s: Pick<StepDTO, 'stageDefId'>) => string | null;
  reason: (id: string | null | undefined) => Reason | undefined;
  reasonLabel: (id: string | null | undefined) => string;
  reasons: Reason[];
  user: (id: string | null | undefined) => DirectoryUser | undefined;
  userName: (id: string | null | undefined) => string;
  /** Who to call about a department's stages: the first active owner holding it. */
  ownerOf: (departmentId: string | null | undefined) => DirectoryUser | undefined;
  planner: () => DirectoryUser | undefined;
  toolType: (id: string | null | undefined) => ToolType | undefined;
  toolDefs: StageDef[];
  projectDefs: StageDef[];
}

export function useLookups(): Lookups | null {
  const { data } = useBootstrap();
  const { lang } = useI18n();
  return useMemo(() => {
    if (!data) return null;
    const defs = new Map(data.stageDefs.map((d) => [d.id, d]));
    const depts = new Map(data.departments.map((d) => [d.id, d]));
    const reasons = new Map(data.reasons.map((r) => [r.id, r]));
    const users = new Map(data.directory.map((u) => [u.id, u]));
    const types = new Map(data.toolTypes.map((t) => [t.id, t]));
    const nameOf = (x: { nameEn: string; nameHi: string } | undefined) =>
      x ? (lang === 'hi' ? x.nameHi : x.nameEn) : '—';
    const sorted = (kind: 'tool' | 'project') =>
      data.stageDefs.filter((d) => d.kind === kind && d.active).sort((a, b) => a.sort - b.sort);
    return {
      b: data,
      tz: data.plant.timezone,
      cal: data.calendar,
      today: () => plantToday(now(), data.plant.timezone),
      stageDef: (id) => defs.get(id),
      stageName: (s) => s.label ?? nameOf(defs.get(s.stageDefId)),
      stageCode: (s) => s.label ?? defs.get(s.stageDefId)?.code ?? '—',
      department: (id) => (id ? depts.get(id) : undefined),
      deptName: (id) => nameOf(id ? depts.get(id) : undefined),
      deptOf: (s) => defs.get(s.stageDefId)?.departmentId ?? null,
      reason: (id) => (id ? reasons.get(id) : undefined),
      reasonLabel: (id) => {
        const r = id ? reasons.get(id) : undefined;
        return r ? (lang === 'hi' ? r.labelHi : r.labelEn) : '';
      },
      reasons: data.reasons.filter((r) => r.active).sort((a, b) => a.sort - b.sort),
      user: (id) => (id ? users.get(id) : undefined),
      userName: (id) => (id ? (users.get(id)?.name ?? '—') : '—'),
      ownerOf: (deptId) =>
        deptId
          ? data.directory.find(
              (u) => u.active && u.roles.includes('owner') && u.departmentIds.includes(deptId),
            )
          : undefined,
      planner: () => data.directory.find((u) => u.active && u.roles.includes('planner')),
      toolType: (id) => (id ? types.get(id) : undefined),
      toolDefs: sorted('tool'),
      projectDefs: sorted('project'),
    };
  }, [data, lang]);
}
